import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { usePublicLegalDocument } from "@/api/queries/legal";
import { renderHelpMarkdown } from "@/help/helpMarkdown";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { formatCalendarDate, legalVersionNumber } from "@/lib/labels";
import { useSeo } from "@/lib/seo";
import { useShellNav } from "@/shell/useShellNav";

/**
 * Slug veřejné stránky. Odpovídá ``legal_document_kinds.public_slug``,
 * kromě ``howItWorks`` — ta je informační, ne právní, druh pro ni
 * neexistuje a jede rovnou z i18n.
 */
type LegalKey =
  | "privacy"
  | "terms"
  | "gdpr"
  | "dpa"
  | "cookies"
  | "complaints"
  | "withdrawal"
  | "howItWorks";

/**
 * Veřejná právní / informační stránka.
 *
 * **Zdrojem je databáze**, ne i18n. Tatáž adresa vydává dokument toho,
 * kdo web provozuje: na apexu platformy její text, na shopu tenanta
 * jeho obchodní podmínky (nebo platformní šablonu s doplněnými údaji
 * jeho firmy). Do teď oba shelly renderovaly tentýž zkompilovaný
 * `legal` namespace, takže zákazník na `{slug}.forgmatic.com` četl
 * obchodní podmínky **platformy** včetně cizího správce údajů.
 *
 * i18n zůstává **fallbackem**, dokud nebudou znění naplněná (fáze F2
 * specu `docs/specs/spec_legal_documents.md`). Bez něj by se stránka
 * po nasazení vyprázdnila dřív, než do ní někdo stihne napsat text —
 * a prázdné obchodní podmínky jsou horší než zastaralé.
 *
 * `howItWorks` je informační, ne právní, a druh dokumentu pro něj
 * neexistuje; jede rovnou z i18n.
 */
export function LegalPage({ pageKey }: { pageKey: LegalKey }): JSX.Element {
  const { t, i18n } = useTranslation("legal");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  // Slug na veřejné cestě odpovídá ``legal_document_kinds.public_slug``.
  const slug = pageKey === "howItWorks" ? null : pageKey;
  // ``?v=`` vydá konkrétní starší znění. Odkazuje sem potvrzení
  // objednávky: bez toho by se zákazník po změně podmínek dostal jen
  // k novým a doklad o tom, co přijal, by mu byl k ničemu.
  const [search] = useSearchParams();
  const pinnedVersion = search.get("v");
  const doc = usePublicLegalDocument(slug, lang, pinnedVersion);

  // i18next vrací u chybějícího klíče sám klíč. Slugy, které vznikly až
  // s DB dokumenty (`cookies`, `complaints`, `withdrawal`), v namespace
  // `legal` záznam nemají — a je lepší to přiznat než vykreslit stránku
  // s nadpisem „complaints.title" nebo úplně prázdnou.
  const rawTitle = t(`${pageKey}.title`);
  const hasFallback = rawTitle !== `${pageKey}.title`;
  const fallbackTitle = hasFallback ? rawTitle : "";
  const fallbackLead = hasFallback ? t(`${pageKey}.lead`) : "";
  const fallbackSections = (
    hasFallback
      ? (i18n.t(`${pageKey}.sections`, {
          returnObjects: true,
          ns: "legal",
        }) ?? [])
      : []
  ) as Array<{ heading: string; body: string }>;

  const fromDb = doc.data ?? null;
  // KAN-1681 — dokud dotaz běží, nevíme, který ze tří stavů to bude.
  // Tvrdit „není k dispozici" (nebo ukázat záložní text platformy na
  // obchodu tenanta) by byla nepravda; je to prostě „načítám".
  // ``slug === null`` (jak to funguje) se na API neptá — vypnutý dotaz
  // zůstává ``isPending`` navždy, a načítat tu nic není.
  const loading = slug !== null && doc.isPending;
  const title = loading
    ? t("legal:loading", "Načítám dokument…")
    : (fromDb?.title ??
      (hasFallback
        ? fallbackTitle
        : t("legal:notPublished.title", "Dokument není k dispozici")));

  // Ani dokument z DB, ani i18n fallback — stránka vykresluje svůj
  // stav „zatím nezveřejněno". Ta odpovídá HTTP 200, ale dokument to
  // není: v indexu by z ní byla trvalá stránka tvrdící, že dokument
  // neexistuje, i poté, co ho platforma publikuje. Stejný důvod, proč
  // ho nese 404 catch-all (``docs/SEO.md``).
  //
  // Až po doběhnutí dotazu: během ``isPending`` se ještě neví, který
  // ze tří stavů to bude, a ``noindex`` napsaný „pro jistotu" by
  // v SPA navigaci zůstal viset nad další stránkou.
  const notPublished = !fromDb && !hasFallback && !doc.isPending;

  // KAN-1955 — popisek pro vyhledávače u právních stránek platformy. Na
  // obchodě tenanta je dokument jeho, tak zůstává jeho popisek obchodu.
  const onPlatform = useShellNav().shell === "platform";
  const platformDescription =
    onPlatform && slug ? t(`platformSeo.${slug}`, { defaultValue: "" }) : "";

  useSeo({
    title: `${title} · ${t("common:app.name", { defaultValue: "Forgmatic" })}`,
    // Popisek z těla dokumentu nebereme — je to celý právní text.
    // Fallbackový lead je krátký a k tomu určený.
    description: platformDescription || fallbackLead,
    path: `/${pageKey === "howItWorks" ? "how-it-works" : pageKey}`,
    lang,
    // ``follow``: odkazy v patičce vedou na dokumenty, které
    // publikované jsou — crawler po nich má jít dál.
    robots: notPublished ? "noindex, follow" : undefined,
  });

  return (
    <main className="mx-auto max-w-3xl px-7 py-10">
      <article className="space-y-6" data-testid="legal-article">
        <h1 className="text-3xl font-semibold tracking-tight text-ink">
          {title}
        </h1>

        {/* KAN-1937 — verze a den účinnosti. Podmínky slibují oznamovat
            změny předem; bez data na stránce nejde poznat, které znění
            platí. Den účinnosti = ``published_at``: dokument platí od
            chvíle, kdy ho platforma zveřejnila. */}
        {fromDb && (
          <p className="text-sm text-ink-3" data-testid="legal-version">
            {fromDb.published_at
              ? t("legal:versionLine", {
                  version: legalVersionNumber(fromDb.version),
                  date: formatCalendarDate(fromDb.published_at, lang),
                })
              : t("legal:versionOnly", {
                  version: legalVersionNumber(fromDb.version),
                })}
          </p>
        )}

        {/* Jazykový fallback se přiznává, ne zamlčuje (KAN-403): text
            je závazný a čtenář musí vědět, že ho nečte ve svém jazyce. */}
        {fromDb?.is_lang_fallback && (
          <p
            className="rounded-md border border-warn-border bg-warn-bg px-3 py-2 text-sm text-warn"
            role="status"
            data-testid="legal-lang-fallback"
          >
            {t("legal:langFallback", {
              defaultValue:
                "Tento dokument zatím není přeložen do vašeho jazyka. Zobrazuje se znění v jazyce {{lang}}.",
              lang: fromDb.lang.toUpperCase(),
            })}
          </p>
        )}

        {loading ? (
          <p className="text-ink-3" role="status" data-testid="legal-loading">
            {t("legal:loading", "Načítám dokument…")}
          </p>
        ) : fromDb ? (
          // Markdown v podmnožině `helpMarkdown` — renderuje na React
          // nodes, ne přes `dangerouslySetInnerHTML`, takže text psaný
          // tenantem nemá injection plochu.
          <div
            className="space-y-3 text-sm leading-relaxed text-ink-2"
            data-testid="legal-body-db"
          >
            {renderHelpMarkdown(fromDb.body)}
          </div>
        ) : hasFallback ? (
          <>
            <p className="text-ink-2">{fallbackLead}</p>
            {Array.isArray(fallbackSections) &&
              fallbackSections.map((s, i) => (
                <section key={i} className="space-y-2">
                  <h2 className="text-lg font-semibold text-ink">{s.heading}</h2>
                  {s.body.split("\n").map((line, j) => (
                    <p key={j} className="text-sm leading-relaxed text-ink-2">
                      {line}
                    </p>
                  ))}
                </section>
              ))}
          </>
        ) : (
          <p className="text-ink-2" data-testid="legal-not-published">
            {t(
              "legal:notPublished.body",
              "Tento dokument zatím nebyl zveřejněn. Zkuste to prosím později, nebo nás kontaktujte.",
            )}
          </p>
        )}
      </article>
    </main>
  );
}
